/**
 * Individual thread view at `/inbox/threads/:id`.
 *
 * Two-column-on-desktop / stacked-on-mobile conversation between a
 * service provider and a client. Auto-marks-read on mount, polls
 * every 8s for new messages, supports text + image attachments
 * (up to 6 photos per message), and shows a soft PII warning when
 * the user types a phone number or email so we nudge them to keep
 * the conversation on-platform.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, PaperPlaneRightIcon as PaperPlaneRight, ImageIcon as ImageIcon, XIcon as XIcon, WarningIcon as Warning, ShieldCheckIcon as ShieldCheck, SparkleIcon as Sparkle } from "@phosphor-icons/react";

const PII_RX = /(\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b|\(\d{3}\)\s?\d{3}[-.\s]?\d{4}|\b[\w.+-]+@[\w-]+\.[\w.-]+\b)/i;

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  return same
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function ThreadView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState(null);
  const [youAre, setYouAre] = useState("");
  const [blocked, setBlocked] = useState(false);

  // Hydrate block state so the header label is truthful across sessions.
  useEffect(() => {
    if (!thread || !youAre) return;
    const otherId = youAre === "provider" ? thread.client_user_id : thread.provider_user_id;
    if (!otherId) return;
    api.get("/me/blocked-users", { silent: true })
      .then((r) => setBlocked(((r.data?.blocked) || []).some((b) => b.user_id === otherId)))
      .catch(() => {});
  }, [thread, youAre]);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]); // [{url}]
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const scrollerRef = useRef(null);

  const loadAll = (silent = false) => {
    api.get(`/provider-threads/${id}/messages`, { silent })
      .then((r) => {
        setMessages(r.data?.messages || []);
        setThread(r.data?.thread || null);
        setYouAre(r.data?.you_are || "");
      })
      .catch((e) => {
        if (e?.response?.status === 404) {
          toast.error("Conversation not found");
          navigate("/inbox/threads");
        }
      });
  };

  useEffect(() => {
    loadAll();
    // mark-read fires once on entry, then again whenever new messages stream in via poll
    api.post(`/provider-threads/${id}/read`).catch(() => {});
    const poll = setInterval(() => loadAll(true), 8_000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-scroll to the bottom whenever messages array grows
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages?.length]);

  const piiDetected = useMemo(() => PII_RX.test(body), [body]);

  const partnerName = thread
    ? (youAre === "provider" ? thread.client_name : thread.provider_name) || "—"
    : "";

  const onPickFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(files).slice(0, 6 - attachments.length)) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/provider-threads/${id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, { url: r.data.url }]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't attach that photo");
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const text = body.trim();
    if (!text && attachments.length === 0) return;
    setSending(true);
    try {
      const r = await api.post(`/provider-threads/${id}/messages`, {
        body: text,
        attachments: attachments.map((a) => a.url),
      });
      setMessages((prev) => [...(prev || []), r.data]);
      setBody("");
      setAttachments([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that message");
    } finally {
      setSending(false);
    }
  };

  const onEnter = (e) => {
    // Cmd/Ctrl + Enter sends, plain Enter wraps. We default to send-on-Enter
    // because that's how every messaging app on earth works, and Shift+Enter
    // gives the user a newline escape hatch.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-6 font-manrope text-ink">
        <Link
          to="/inbox/threads"
          className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-ink mb-3"
          data-testid="thread-back"
        >
          <ArrowLeft size={14} /> All conversations
        </Link>

        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold">
              {youAre === "provider" ? "Client" : "Service Provider"}
            </p>
            <h1 className="font-spectral text-2xl tracking-tight truncate" data-testid="thread-partner-name">
              {thread ? partnerName : <Skeleton className="h-7 w-40" />}
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {thread && (
              <button
                type="button"
                onClick={async () => {
                  const otherId = youAre === "provider" ? thread.client_user_id : thread.provider_user_id;
                  if (!otherId) return;
                  if (blocked) {
                    await api.delete(`/users/${otherId}/block`).catch(() => {});
                    setBlocked(false);
                    toast.success("Unblocked — they can message you again");
                  } else {
                    if (!window.confirm(`Block ${partnerName}? Neither of you will be able to message the other.`)) return;
                    try {
                      await api.post(`/users/${otherId}/block`);
                      setBlocked(true);
                      toast.success(`${partnerName} blocked`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Couldn't block");
                    }
                  }
                }}
                className="text-xs text-[#666] hover:text-[#C1272D] underline underline-offset-2 decoration-dotted"
                data-testid="thread-block-toggle"
              >
                {blocked ? "Unblock" : "Block"}
              </button>
            )}
            {thread && (
              <button
                type="button"
                onClick={async () => {
                  const reason = window.prompt(
                    "What's wrong with this conversation? (1-2 sentences — Armstrong staff will review)"
                  );
                  if (!reason?.trim()) return;
                  try {
                    await api.post(`/provider-threads/${id}/report`, { reason: reason.trim() });
                    toast.success("Reported. Armstrong staff will review within 24 hours.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't submit report");
                  }
                }}
                className="text-xs text-[#666] hover:text-[#C1272D] underline underline-offset-2 decoration-dotted"
                data-testid="thread-report"
              >
                Report
              </button>
            )}
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <ShieldCheck size={12} weight="fill" /> Armstrong-moderated
            </span>
          </div>
        </div>

        {/* Message scroller */}
        <div
          ref={scrollerRef}
          className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-2xl p-4 sm:p-5 mb-4 overflow-y-auto"
          style={{ height: "min(60vh, 540px)" }}
          data-testid="thread-scroller"
        >
          {messages === null ? (
            <div className="space-y-3">
              <Skeleton className="h-14 w-2/3" />
              <Skeleton className="h-14 w-1/2 ml-auto" />
              <Skeleton className="h-14 w-3/4" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-sm text-[#888] py-10">
              <Sparkle size={28} className="mx-auto text-gold mb-2" />
              Say hi 👋 — your first message starts the conversation.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {messages.map((m) => (
                <Bubble key={m.id} m={m} mine={m.sender_side === youAre} />
              ))}
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-3 sm:p-4" data-testid="thread-composer">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {attachments.map((a, i) => (
                <div key={i} className="relative h-16 w-16 rounded-md overflow-hidden">
                  <img src={resolveUrl(a.url)} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 bg-black/80 hover:bg-black text-white rounded-full p-0.5"
                    data-testid={`thread-attachment-remove-${i}`}
                    aria-label="Remove attachment"
                  >
                    <XIcon size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {piiDetected && (
            <div className="flex items-start gap-2 mb-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs" data-testid="thread-pii-warning">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
              <span>
                Looks like you're about to share a phone number or email. Keep your conversation on Armstrong so we can step in if anything goes wrong — and you'll always have a paper trail.
              </span>
            </div>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onEnter}
            placeholder={
              youAre === "provider"
                ? "Type a message to your client… (Enter to send, Shift+Enter for newline)"
                : "Type a message to your service pro…"
            }
            rows={2}
            className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 shadow-none text-sm"
            data-testid="thread-input"
          />
          <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-[#EEE]">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
              data-testid="thread-file-input"
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading || attachments.length >= 6}
              className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-ink disabled:opacity-50"
              data-testid="thread-attach-btn"
            >
              <ImageIcon size={18} />
              {uploading ? "Uploading…" : "Photo"}
            </button>
            <Button
              onClick={send}
              disabled={sending || (!body.trim() && attachments.length === 0)}
              data-testid="thread-send-btn"
            >
              <PaperPlaneRight size={14} className="mr-1.5" weight="fill" />
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Bubble({ m, mine }) {
  if (m.sender_side === "system") {
    return (
      <div className="self-center max-w-md text-center text-xs text-[#777] bg-white border border-[#E5E5E5] rounded-full px-3 py-1.5 inline-flex items-center gap-1.5" data-testid={`thread-msg-${m.id}`}>
        <Sparkle size={12} weight="fill" className="text-gold" />
        {m.body}
      </div>
    );
  }
  return (
    <div
      className={`max-w-[80%] ${mine ? "self-end" : "self-start"}`}
      data-testid={`thread-msg-${m.id}`}
    >
      <div
        className={
          "rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm " +
          (mine
            ? "bg-[#0E0E0E] text-white rounded-br-sm"
            : "bg-white border border-[#E5E5E5] text-ink rounded-bl-sm")
        }
      >
        {m.attachments && m.attachments.length > 0 && (
          <div className={`grid ${m.attachments.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-1.5 mb-2`}>
            {m.attachments.map((url, i) => (
              <a
                key={i}
                href={resolveUrl(url)}
                target="_blank"
                rel="noreferrer"
                className="block rounded-md overflow-hidden"
              >
                <img src={resolveUrl(url)} alt="" className="w-full h-auto block max-h-48 object-cover" />
              </a>
            ))}
          </div>
        )}
        {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
      </div>
      <p className={`text-[10px] mt-1 px-1 ${mine ? "text-right text-[#888]" : "text-left text-[#888]"}`}>
        {fmtTime(m.created_at)}
        {m.pii_warning && (
          <span className="ml-1.5 inline-flex items-center gap-0.5 text-amber-700">
            <Warning size={9} weight="fill" /> contact-info shared
          </span>
        )}
      </p>
    </div>
  );
}
